// src/components/CatalogHealth.tsx — 00040 §6–7: catalog integrity check + catalog export (HSC-01).
// Reads ops_catalog_integrity (live report + last weekly run) and ops_catalog_export (one row per service).
import { useCallback, useEffect, useState } from "react";
import { ops, type IntegrityItem, type IntegrityReport } from "../lib/ops";
import { Notice, SectionLabel } from "./ui";

function toCsv(rows: Record<string, unknown>[]): string {
  if (!rows.length) return "";
  const cols = Object.keys(rows[0]);
  const esc = (v: unknown) => {
    const s = v === null || v === undefined ? "" : Array.isArray(v) ? v.join(" ") : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
}

export function CatalogHealth() {
  const [report, setReport] = useState<IntegrityReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setReport(await ops.catalogIntegrity());
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const download = async () => {
    setBusy(true);
    try {
      const res = await ops.catalogExport();
      const blob = new Blob([toCsv(res.rows)], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `gm-catalog-export_${res.exported_at.slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const groups = new Map<string, IntegrityItem[]>();
  for (const it of report?.items ?? []) {
    const k = `${it.severity}|${it.check_id}|${it.check_name}`;
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k)!.push(it);
  }

  return (
    <section className="editor-section card" style={{ marginBottom: 16 }}>
      <SectionLabel>Catalog health</SectionLabel>
      {error && <Notice kind="error">{error}</Notice>}
      {report && (
        <>
          <p className="framing">
            {report.errors === 0 && report.warnings === 0
              ? "No gaps found."
              : `${report.errors} error${report.errors === 1 ? "" : "s"} (block a product launch) · ${report.warnings} warning${report.warnings === 1 ? "" : "s"}.`}
            {report.last_run ? ` Weekly check last ran ${report.last_run.run_at.slice(0, 10)}.` : ""}
          </p>
          {Array.from(groups.entries()).map(([k, items]) => {
            const [sev, id, name] = k.split("|");
            return (
              <details key={k} style={{ marginBottom: 6 }}>
                <summary>
                  <b style={{ color: sev === "error" ? "var(--rust, #9a3b2a)" : "inherit" }}>{sev === "error" ? "Error" : "Warning"}</b> {id} · {name} ({items.length})
                </summary>
                <ul className="hint" style={{ marginTop: 6 }}>
                  {items.map((it) => (
                    <li key={it.target}>
                      <code>{it.target}</code> — {it.detail}
                    </li>
                  ))}
                </ul>
              </details>
            );
          })}
        </>
      )}
      <div style={{ marginTop: 10 }}>
        <button type="button" className="btn" disabled={busy} onClick={download}>
          {busy ? "Preparing…" : "Download catalog export (CSV)"}
        </button>
        <span className="hint" style={{ marginLeft: 10 }}>Replaces the service tabs of the naming sheet (00024) and the master plan.</span>
      </div>
    </section>
  );
}
